import { createContext, useContext, ReactNode, useMemo, useCallback } from "react";
import { useUploadFileMutation } from "@hooks";

interface ImageUploadContextValue {
  orgId: string;
  uploadImage: (file: File) => Promise<{ url: string; fileName: string }>;
  isUploading: boolean;
}

const ImageUploadContext = createContext<ImageUploadContextValue | null>(null);

export function useImageUploadContext() {
  const context = useContext(ImageUploadContext);
  if (!context) {
    throw new Error(
      "useImageUploadContext must be used within an ImageUploadProvider"
    );
  }
  return context;
}

interface ImageUploadProviderProps {
  children: ReactNode;
  orgId: string;
}

export function ImageUploadProvider({
  children,
  orgId,
}: ImageUploadProviderProps) {
  const uploadFileMutation = useUploadFileMutation();

  const uploadImage = useCallback(
    async (file: File): Promise<{ url: string; fileName: string }> => {
      const uploadResult = await uploadFileMutation.mutateAsync({ orgId, file });
      const shareToken = uploadResult.shareLink?.token;

      if (!shareToken) {
        throw new Error("Failed to get file share link");
      }

      return {
        url: `${window._env_.BACKEND_BASE_URL}/share/${shareToken}`,
        fileName: file.name,
      };
    },
    [orgId, uploadFileMutation]
  );

  const isUploading = uploadFileMutation.isPending;

  const value = useMemo(
    () => ({ orgId, uploadImage, isUploading }),
    [orgId, uploadImage, isUploading]
  );

  return (
    <ImageUploadContext.Provider value={value}>
      {children}
    </ImageUploadContext.Provider>
  );
}
